// Libs
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
// Others
import { RootState } from '~/utils/redux/store';
import {
  changeStatusJob,
  createJob,
  deleteJob,
  getJobDetail,
  getJobDetailUpdate,
  getJobList,
  getJobListByCustomer,
  getScheduleJobList,
  updateJob,
} from '~/thunks/job/jobThunk';
import { IJobDetail } from '~/utils/interface/job';
import { DateFormatEnum } from '~/utils/enum/common';
import { ISchedule, IScheduleTimeLine } from '~/utils/interface/schedule';
import { CalendarViewMode, ScheduleMode } from '~/utils/type/common';
import { DEFAULT_VIEW_MODE, SCHEDULE_MODE_FULL } from '~/utils/constants/common';

export interface IJobState {
  isRefreshJobList: boolean;
  isRefreshJobByCustomerList: boolean;
  jobDetail: IJobDetail;
  scheduleCurrentTime: string;
  isRefreshScheduleJob: boolean;
  scheduleJob: IScheduleTimeLine[];
  calendarViewMode: CalendarViewMode;
  scheduleMode: ScheduleMode;
  events: ISchedule[];
}

export const initialState: IJobState = {
  isRefreshJobList: false,
  isRefreshJobByCustomerList: false,
  jobDetail: {} as IJobDetail,
  scheduleCurrentTime: moment().format(DateFormatEnum.YYYY_MM_DD),
  isRefreshScheduleJob: false,
  scheduleJob: [],
  calendarViewMode: DEFAULT_VIEW_MODE,
  scheduleMode: SCHEDULE_MODE_FULL,
  events: [],
};

const jobSlice = createSlice({
  name: 'job',
  initialState,
  reducers: {
    setRefreshJobList: (state, action) => {
      state.isRefreshJobList = action.payload;
    },
    setRefreshJobByCustomerList: (state, action) => {
      state.isRefreshJobByCustomerList = action.payload;
    },
    setScheduleCurrentTime(state, action: PayloadAction<string>) {
      state.scheduleCurrentTime = action.payload;
    },
    setIsRefreshScheduleData(state, action: PayloadAction<boolean>) {
      state.isRefreshScheduleJob = action.payload;
    },
    changeScheduleJob(state, action: PayloadAction<IScheduleTimeLine[]>) {
      state.scheduleJob = action.payload;
    },
    changeEvents(state, action: PayloadAction<ISchedule[]>) {
      state.events = action.payload;
    },
    changeScheduleMode(state, action: PayloadAction<ScheduleMode>) {
      state.scheduleMode = action.payload;
    },
    changeCalendarViewMode(state, action: PayloadAction<CalendarViewMode>) {
      state.scheduleCurrentTime = moment().format(DateFormatEnum.YYYY_MM_DD);
      state.calendarViewMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    // POST: Create job
    builder
      .addCase(createJob.pending, (state) => {})
      .addCase(createJob.fulfilled, (state) => {})
      .addCase(createJob.rejected, (state) => {});

    // GET: Get list job
    builder
      .addCase(getJobList.pending, (state) => {})
      .addCase(getJobList.fulfilled, (state) => {})
      .addCase(getJobList.rejected, (state) => {});

    // GET: Get job detail
    builder
      .addCase(getJobDetail.pending, (state) => {})
      .addCase(getJobDetail.fulfilled, (state, action) => {
        state.jobDetail = {
          ...state.jobDetail,
          ...action.payload.data,
        };
      })
      .addCase(getJobDetail.rejected, (state) => {});

    // GET: Get list job by customer
    builder
      .addCase(getJobListByCustomer.pending, (state) => {})
      .addCase(getJobListByCustomer.fulfilled, (state) => {})
      .addCase(getJobListByCustomer.rejected, (state) => {});

    // GET: Get schedule job list
    builder
      .addCase(getScheduleJobList.pending, (state) => {})
      .addCase(getScheduleJobList.fulfilled, (state, action) => {
        if (state.calendarViewMode === 'month') {
          state.events = action.payload.data as ISchedule[];
        } else if (state.calendarViewMode === 'day' || state.calendarViewMode === 'week') {
          state.scheduleJob = action.payload.data as IScheduleTimeLine[];
        }
        state.isRefreshScheduleJob = false;
      })
      .addCase(getScheduleJobList.rejected, (state) => {});

    // DELETE: Delete job
    builder
      .addCase(deleteJob.pending, (state) => {})
      .addCase(deleteJob.fulfilled, (state) => {})
      .addCase(deleteJob.rejected, (state) => {});

    // PUT: Update job
    builder
      .addCase(updateJob.pending, (state) => {})
      .addCase(updateJob.fulfilled, (state) => {})
      .addCase(updateJob.rejected, (state) => {});

    // PUT: Change status job
    builder
      .addCase(changeStatusJob.pending, (state) => {})
      .addCase(changeStatusJob.fulfilled, (state) => {})
      .addCase(changeStatusJob.rejected, (state) => {});

    // GET: Get job detail update
    builder
      .addCase(getJobDetailUpdate.pending, (state) => {})
      .addCase(getJobDetailUpdate.fulfilled, (state) => {})
      .addCase(getJobDetailUpdate.rejected, (state) => {});
  },
});

// Action
export const jobActions = jobSlice.actions;

// Selectors
export const selectIsRefreshJobList = (state: RootState) => state.job.isRefreshJobList;
export const selectIsRefreshJobByCustomerList = (state: RootState) => state.job.isRefreshJobByCustomerList;
export const selectJobDetail = (state: RootState) => state.job.jobDetail;

// Reducer
const jobReducer = jobSlice.reducer;
export default jobReducer;
